/**
 * OD（外包）角色的页面权限白名单过滤。
 *
 * 背景：测试 OD / 研发 OD 是"外包"型角色，后端下发的 pagePermissions 是全量研发角色权限；
 * 前端需要额外按"外包可见页面"进行二次裁剪，避免外包人员看到不应看到的页面。
 *
 * 两种 OD 的差异：
 *   - test-od：测试外包，能看测试计划（TestPlan）相关页面，不能看需求/提测/测试报告/自动化测试
 *   - dev-od：研发外包，能看需求/测试方案/提测/测试报告/自动化测试 + 测试计划页面
 *
 * 共同约束：
 *   - 两种 OD 均不可见「项目质量看板」（ProjectDashboard，2026-04-22 产品决策）。
 *     进入项目空间时由 sites/project/components/layout/index.vue 兜底落到首个可访问子页；
 *     工作台点项目卡片硬编码跳 ProjectDashboard 的场景由 permission-guard 自动重定向。
 *   - 两种 OD 均可见 TestPlan / TestPlanDetail / TestPlanReport：
 *     · test-od 本就需要（测试外包的核心工作域）
 *     · dev-od 2026-04-25 修订加入——开发自测计划（stage=selftest）必须在该页把状态标 Done，
 *       dev-od 才能在提测表单里选自测计划完成「过自测用例」→ 提测的链路。
 *       feature 决策 1 初版漏点详见 docs/issues/2026-04-25.md。
 *
 * 非 OD 角色：原样返回，不做任何过滤。
 */

import { PermissionPageKey, PermRoleKey } from '@/types/permission';

const TEST_OD_WHITELIST: ReadonlySet<string> = new Set([
  PermissionPageKey.Project,
  PermissionPageKey.ProjectDev,
  PermissionPageKey.ProjectTestPlan,
  PermissionPageKey.ProjectTestPlanDetail,
  PermissionPageKey.ProjectTestPlanReport,
  PermissionPageKey.ProjectIssue,
  PermissionPageKey.ProjectIssueList,
  PermissionPageKey.ProjectIssueDetail,
  PermissionPageKey.CloudtestTestCase,
  PermissionPageKey.CloudtestTestCaseMind,
  PermissionPageKey.Issue,
  PermissionPageKey.IssueDetail,
]);

const DEV_OD_WHITELIST: ReadonlySet<string> = new Set([
  PermissionPageKey.Project,
  PermissionPageKey.ProjectDev,
  PermissionPageKey.ProjectDemandManage,
  PermissionPageKey.ProjectTestProgramme,
  PermissionPageKey.ProjectSubmitTest,
  PermissionPageKey.ProjectTestPlan,
  PermissionPageKey.ProjectTestPlanDetail,
  PermissionPageKey.ProjectTestPlanReport,
  PermissionPageKey.ProjectTestReport,
  PermissionPageKey.ProjectTestReportDetail,
  PermissionPageKey.ProjectAutoTest,
  PermissionPageKey.CloudtestTestCase,
  PermissionPageKey.CloudtestTestCaseMind,
  PermissionPageKey.ProjectIssue,
  PermissionPageKey.ProjectIssueList,
  PermissionPageKey.ProjectIssueDetail,
  PermissionPageKey.Issue,
  PermissionPageKey.IssueDetail,
]);

/**
 * 按 OD 角色白名单过滤页面权限。
 * @param roleId 当前用户主角色 id（userStore.current.roles[0].id）
 * @param permissions 后端下发的 pagePermissions 列表
 * @returns OD 角色返回白名单过滤后的子集；非 OD 角色原样返回
 */
export function filterPermissionsByOdRole(roleId: string, permissions: string[]): string[] {
  if (roleId === PermRoleKey.TestOd) {
    return permissions.filter((p) => TEST_OD_WHITELIST.has(p));
  }
  if (roleId === PermRoleKey.DevOd) {
    return permissions.filter((p) => DEV_OD_WHITELIST.has(p));
  }
  return permissions;
}

/**
 * 判断一个角色 id 是否属于 OD 外包角色（test-od / dev-od）。
 *
 * 用于 `permission-guard.ts` 放宽"智能重定向"的触发条件：
 *   if (isOrganizationUser() || isOdRoleId(currentRoleId)) { findAccessibleChildRoute(...) }
 *
 * OD 外包用户没有 organizationIds，原本落入 403；本判断把 OD 纳入智能重定向覆盖范围，
 * 解决"工作台点项目卡片硬编码跳 ProjectDashboard 却没权限" → 403 的 bug。
 *
 * @param roleId 主角色 id（userStore.current.roles[0].id），允许 undefined（未登录场景）
 * @returns 是 OD 返回 true；否则 false（包含 undefined / 空串 / 未知角色）
 */
export function isOdRoleId(roleId: string | undefined): boolean {
  return roleId === PermRoleKey.TestOd || roleId === PermRoleKey.DevOd;
}
